import { Hour12 } from '@/shared/filters/hour-12.filter';
import { ScheduleBlockItem, ScheduleBlock } from '@/tools/models/class-builder.models';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Prop } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import { EdGenericCalendarWeekdays } from '@/core/components/ed-generic-calendar/interfaces/ed-generic-calendar-weekdays.interface';

@Component({
  name: 'ed-block-item-group',
  components: {
    EdBlockItem: () => import(/* "EdBlockItem" */ '../ed-block-item/ed-block-item.component.vue'),
    EdBlockItemAdd: () => import(/* "EdBlockItemAdd" */ '../ed-block-item-add/ed-block-item-add.component.vue'),
  }
})
export default class EdBlockItemGroup extends Vue {
  @Prop({ default: () => [] }) readonly blocks: ScheduleBlockItem[];
  @Prop({ required: true }) readonly isAbleToModify: boolean;
  @Prop({ default: () => [] }) readonly currentEntriesCreated: ScheduleBlockItem[];
  @Prop() readonly scheduleBlocksCreated: ScheduleBlock[];
  @Prop() readonly scheduleBlockInEdition: ScheduleBlock;
  @Prop() readonly weekdays: number[] | EdGenericCalendarWeekdays[];
  @Prop() readonly scheduleStartHour: string; 
  @Prop() readonly scheduleEndHour: string;
  @Prop() readonly isWeeklySchedule: boolean;
  
  groupInEdition = '';
  blocksInEdition: ScheduleBlockItem[] = [];

  @Emit('onEditBlockSelected')
  emitOnEditBlockSelected(): boolean {
    return;
  }

  @Emit('onUpdateBlocks')
  emitOnUpdateBlocks(blocks: ScheduleBlockItem[]): ScheduleBlockItem[] {
    return blocks;
  }

  daysOfWeek = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',    
  ];

  daysOfAB = [
    'A',
    'B',
    'AS',
    'AB'
  ];

  get blocksResume() {
    return this.getBlocksResume(this.blocks);
  }

  getBlocksResume(blocks: ScheduleBlockItem[]) {
    const resume = Object.values(blocks.reduce((acc, block) => {
      if (block.toDelete) return acc;

      const key = `${block.start}_${block.end}`;
      const group = acc[key];
  
      if (group) {
        group.label.push(block.day);
      } else {
        acc[key] = {
          label: [block.day],
          start: block.start,
          end: block.end,
        };
      }
  
      return acc;
    }, {} as Record<string, {label: string[], start: string, end: string}>)).map(group => {
      let label = '';
      if (this.isWeeklySchedule) {
        label = this.compressDays(group.label.sort((a, b) => this.daysOfWeek.indexOf(a) - this.daysOfWeek.indexOf(b)));
      } else {
        label = group.label.map(a => this.$t(`tools.classBuilder.${a}`).toString()).join(' - ');
      }

      return {
        id: `${group.start}${group.end}`.replaceAll(':', ''),
        label,
        days: group.label,
        subLabel: `${Hour12(group.start)} - ${Hour12(group.end)}`,
        start: group.start,
        end: group.end
      }
    });

    // Sort by day name / time
    resume.sort((a, b) => {
      const dayA = a.days[0];
      const dayB = b.days[0];
      if (this.isWeeklySchedule) {
        return this.daysOfWeek.indexOf(dayA) - this.daysOfWeek.indexOf(dayB);
      } else {
        if(dayA === dayB)
          return Number(a.id) - Number(b.id);
        else
          return this.daysOfAB.indexOf(dayA) - this.daysOfAB.indexOf(dayB);
      }
    });

    return resume;
  }

  compressDays(days: string[]): string {
    let compressed = '', start = -1, end = -1;
  
    for (let i = 0; i < 7; ++i) {
      if (days.includes(this.daysOfWeek[i])) {
        if (start < 0) start = i;
        end = i;
      } else if (start >= 0) {
        compressed += (compressed ? ', ' : '') + this.daysOfWeek[start] + (end > start ? ' - ' + this.daysOfWeek[end] : '');
        start = end = -1;
      }
    }
  
    if (start >= 0) {
      compressed += (compressed ? ', ' : '') + this.daysOfWeek[start] + (end > start ? ' - ' + this.daysOfWeek[end] : '');
    }
  
    return compressed;
  }

  onEditBlockGroup(block: any) {
    this.blocksInEdition = this.getBlocksByStartAndEnd(block.start, block.end);
    this.groupInEdition = block.id;
    this.emitOnEditBlockSelected();
  }

  getBlocksByStartAndEnd(startHour: string, endHour: string) {
    return this.blocks.filter(
      block => block.start === startHour && block.end === endHour
    );
  }

  onSaveChanges({ add }) {
    const blocksUpdated = this.blocks.filter(
      b => !this.blocksInEdition.find(bi => bi.start === b.start && bi.end === b.end) 
    );
    
    blocksUpdated.push(...add);
    this.emitOnUpdateBlocks(blocksUpdated);
    this.hideEditionMode();
  }

  onRemoveBlocks(block: any) {
    let blocks = cloneDeep(this.blocks);
    const blocksToUpdate = blocks.filter(b => {
      if (b.start === block.start && b.end === block.end && block.days.includes(b.day)) {
        if (b.id) {
          b.toDelete = true;

          return true;
        }

        return false;
      }

      return true;
    });

    this.emitOnUpdateBlocks(blocksToUpdate);
  }

  hideEditionMode() {
    this.groupInEdition = '';
  }
}